import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useCreateIgreja } from './use-admin'
import { logger } from '@/lib/logger'
import { toast } from 'sonner'
import { formatPhone } from '@/lib/utils'

export interface NovaIgrejaForm {
  nome: string
  descricao: string
  endereco: string
  cidade: string
  estado: string
  cep: string
  telefone: string
  email: string
  website: string
  status: 'ativa' | 'inativa'
  cor_primaria: string
  cor_secundaria: string
}

export const NOVA_IGREJA_DEFAULT: NovaIgrejaForm = {
  nome: '',
  descricao: '',
  endereco: '',
  cidade: '',
  estado: '',
  cep: '',
  telefone: '',
  email: '',
  website: '',
  status: 'ativa',
  cor_primaria: '#3b82f6',
  cor_secundaria: '#64748b',
}

export function useIgrejaNova() {
  const navigate = useNavigate()
  const [formState, setFormState] = useState<NovaIgrejaForm>(NOVA_IGREJA_DEFAULT)
  const [currentStep, setCurrentStep] = useState(1)
  const [errors, setErrors] = useState<Record<string, string>>({})

  const createIgrejaMutation = useCreateIgreja()

  const handleInputChange = (field: keyof NovaIgrejaForm, value: string) => {
    let processedValue = value
    
    // Apply phone mask if field is telefone
    if (field === 'telefone') {
      processedValue = formatPhone(value)
    }
    
    setFormState((prev) => ({ ...prev, [field]: processedValue }))
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }))
    }
  }

  const validateStep = (step: number): boolean => {
    const newErrors: Record<string, string> = {}

    switch (step) {
      case 1:
        if (!formState.nome.trim()) newErrors.nome = 'Nome da igreja é obrigatório'
        if (!formState.descricao.trim()) newErrors.descricao = 'Descrição é obrigatória'
        break
      case 2:
        if (!formState.endereco.trim()) newErrors.endereco = 'Endereço é obrigatório'
        if (!formState.cidade.trim()) newErrors.cidade = 'Cidade é obrigatória'
        if (!formState.estado) newErrors.estado = 'Estado é obrigatório'
        if (!formState.cep.trim()) newErrors.cep = 'CEP é obrigatório'
        break
      case 3:
        if (!formState.email.trim()) newErrors.email = 'Email é obrigatório'
        if (formState.email && !/\S+@\S+\.\S+/.test(formState.email)) {
          newErrors.email = 'Email inválido'
        }
        if (!formState.telefone.trim()) newErrors.telefone = 'Telefone é obrigatório'
        break
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 4))
    }
  }

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1))
  }

  const resetForm = () => {
    setFormState(NOVA_IGREJA_DEFAULT)
    setCurrentStep(1)
    setErrors({})
  }

  const handleSubmit = async () => {
    if (!validateStep(3)) return

    try {
      // Mapear os dados do formulário para o formato esperado pelo Supabase
      const igrejaData = {
        nome: formState.nome,
        descricao: formState.descricao || null,
        endereco: formState.endereco || null,
        cidade: formState.cidade || null,
        estado: formState.estado || null,
        cep: formState.cep || null,
        telefone: formState.telefone || null,
        email: formState.email || null,
        pastor: null, // Adicionar campo pastor
        dominio: null,
        ativa: formState.status === 'ativa',
        cor_primaria: formState.cor_primaria,
        cor_secundaria: formState.cor_secundaria,
        logo_dark: null,
        logo_light: null,
      }

      await createIgrejaMutation.mutateAsync(igrejaData)

      logger.info('Igreja criada com sucesso', { context: 'IGREJA_NOVA' })
      toast.success('Igreja criada com sucesso!')
      navigate('/admin-app')
    } catch (error) {
      logger.error('Erro ao criar igreja', { context: 'IGREJA_NOVA' })
      console.error('Detalhes do erro:', error)
      toast.error('Erro ao criar igreja. Tente novamente.')
    }
  }

  const progress = (currentStep / 4) * 100

  return {
    // Estado do formulário
    formState,
    currentStep,
    errors,
    progress,
    
    // Estados de loading
    isSubmitting: createIgrejaMutation.isPending,
    
    // Funções
    handleInputChange,
    validateStep,
    nextStep,
    prevStep,
    resetForm,
    handleSubmit,
  }
}
